import { supabase } from './supabase';

export interface VocabRule {
  findText: string;
  replaceText: string;
}

class ApiClient {
  private cachedUserId: string | null = null;
  // 認証完了待機用: AuthContextがsetUserId()を呼ぶまでAPI呼び出しをブロック
  private authReadyResolvers: Array<(userId: string) => void> = [];
  private authFailed = false;

  /** キャッシュをクリア（ログアウト時に呼ぶ） */
  clearCache() {
    this.cachedUserId = null;
    this.authFailed = true;
    // 待機中のAPI呼び出しを全てreject
    this.authReadyResolvers = [];
  }

  /** 外部からキャッシュを設定（AuthContextから呼ぶ） */
  setUserId(userId: string) {
    this.cachedUserId = userId;
    this.authFailed = false;
    // 待機中のAPI呼び出しを全てresolve
    for (const resolve of this.authReadyResolvers) {
      resolve(userId);
    }
    this.authReadyResolvers = [];
  }

  /** 認証が完了しなかったことを通知（タイムアウト時に呼ぶ） */
  notifyAuthFailed() {
    this.authFailed = true;
    this.authReadyResolvers = [];
  }

  private async getUserId(): Promise<string> {
    if (this.cachedUserId) return this.cachedUserId;

    // 1. getSession() はlocalStorage読み取りのみ（高速・ハングしない）
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        this.cachedUserId = session.user.id;
        return session.user.id;
      }
    } catch {
      // getSession失敗時は待機にフォールバック
    }

    // 2. 認証がまだ完了していない → AuthContextがsetUserId()を呼ぶまで待機
    //    OAuthリダイレクト直後はgetSession()もgetUser()も使えないため、
    //    onAuthStateChange(SIGNED_IN) → setUserId() を待つ
    if (this.authFailed) {
      throw Object.assign(new Error('Not authenticated'), { status: 401 });
    }

    return new Promise<string>((resolve, reject) => {
      this.authReadyResolvers.push(resolve);
      setTimeout(() => {
        // タイムアウト: resolversから自分を除去
        const idx = this.authReadyResolvers.indexOf(resolve);
        if (idx !== -1) this.authReadyResolvers.splice(idx, 1);
        reject(new Error('Auth timeout'));
      }, 15000);
    });
  }

  // Auth — kept for interface compat but now uses Supabase Auth
  async login(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
    const user = data.user!;
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    return {
      token: data.session!.access_token,
      user: {
        id: user.id,
        email: user.email || '',
        name: profile?.display_name || user.email?.split('@')[0] || '',
        picture: profile?.picture,
        plan: profile?.premium ? 'premium' : 'free',
        role: profile?.role || 'user',
      } as User,
    };
  }

  async register(email: string, password: string, name: string): Promise<{ token: string; user: User; needsEmailConfirmation?: boolean }> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: name },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    if (error) {
      if (error.message.toLowerCase().includes('rate limit')) {
        throw new Error('メール送信の制限に達しました。しばらく待ってから再度お試しください。');
      }
      throw new Error(error.message);
    }
    const user = data.user;
    if (!user) throw new Error('ユーザーの作成に失敗しました');

    // 既存アカウント検出: identities が空 = そのメールは登録済み
    if (user.identities && user.identities.length === 0) {
      throw new Error('このメールアドレスは既に登録されています。ログインしてください。');
    }

    // メール確認が必要な場合、session は null
    if (!data.session) {
      return {
        token: '',
        user: { id: user.id, email: user.email || '', name, plan: 'free' } as User,
        needsEmailConfirmation: true,
      };
    }

    return {
      token: data.session.access_token,
      user: {
        id: user.id,
        email: user.email || '',
        name,
        plan: 'free',
      } as User,
    };
  }

  async guestLogin() {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) throw new Error(error.message);
    const user = data.user!;
    return {
      token: data.session!.access_token,
      user: {
        id: user.id,
        email: '',
        name: 'ゲスト',
        plan: 'free',
      } as User,
    };
  }

  async resetPasswordForEmail(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    if (error) {
      if (error.message.toLowerCase().includes('rate limit')) {
        throw new Error('メール送信の制限に達しました。しばらく待ってから再度お試しください。');
      }
      throw new Error(error.message);
    }
  }

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw new Error(error.message);
  }

  getOAuthUrl(_provider: 'google' | 'github'): string {
    // Not used directly anymore — see AuthContext for Supabase OAuth flow
    return '';
  }

  async getMe(accessToken?: string): Promise<User> {
    const userId = await this.getUserId();

    // accessToken が渡された場合、Supabase クライアントを経由せず直接 REST API を叩く。
    // SIGNED_IN ハンドラ内では _initialize() 未完了のため
    // supabase.from() 内部の getSession() 待ちでデッドロックする。
    if (accessToken) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(
        `${supabaseUrl}/rest/v1/profiles?id=eq.${userId}&select=*`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'apikey': supabaseAnonKey,
            'Accept': 'application/vnd.pgrst.object+json',
          },
        },
      );
      const profile = res.ok ? await res.json() : null;
      return {
        id: userId,
        email: profile?.email || '',
        name: profile?.display_name || profile?.email?.split('@')[0] || '',
        picture: profile?.picture,
        plan: profile?.premium ? 'premium' : 'free',
        role: profile?.role || 'user',
        featureFlags: profile?.feature_flags || undefined,
      };
    }

    // 通常パス: Supabase クライアント経由（_initialize() 完了後）
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    return {
      id: userId,
      email: profile?.email || '',
      name: profile?.display_name || profile?.email?.split('@')[0] || '',
      picture: profile?.picture,
      plan: profile?.premium ? 'premium' : 'free',
      role: profile?.role || 'user',
      featureFlags: profile?.feature_flags || undefined,
    };
  }

  async updateProfile(updates: { display_name?: string; picture?: string }): Promise<void> {
    const userId = await this.getUserId();
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);
    if (error) throw new Error(error.message);
  }

  async uploadAvatar(file: File): Promise<string> {
    const userId = await this.getUserId();
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${userId}/avatar.${ext}`;

    // Overwrite existing avatar
    const { error } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (error) throw new Error(error.message);

    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(path);

    // Append cache-buster to force browser refresh
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

    // Update profile picture column
    await this.updateProfile({ picture: publicUrl });

    return publicUrl;
  }

  // Entries
  async getEntries(params?: { limit?: number; offset?: number }): Promise<Entry[]> {
    const userId = await this.getUserId();
    let query = supabase
      .from('entries')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });

    if (params?.limit) query = query.limit(params.limit);
    if (params?.offset) query = query.range(params.offset, params.offset + (params.limit || 50) - 1);

    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data || []) as Entry[];
  }

  async getEntry(id: string): Promise<Entry> {
    const userId = await this.getUserId();
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();
    if (error) throw new Error(error.message);
    return data as Entry;
  }

  async getEntriesByDate(date: string): Promise<Entry[]> {
    const userId = await this.getUserId();
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date);
    if (error) throw new Error(error.message);
    return (data || []) as Entry[];
  }

  async createEntry(data: FormData): Promise<CreateEntryResult> {
    console.debug('[api] createEntry: invoking process-entry');

    // セッションを明示的にリフレッシュ（JWT期限切れによる401を防止）
    const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !session) {
      console.error('[api] createEntry: session refresh failed', refreshError);
      throw new Error('セッションが切れました。再ログインしてください。');
    }

    const { data: result, error } = await supabase.functions.invoke('process-entry', {
      body: data,
    });
    if (error) {
      console.error('[api] createEntry error:', error.message, error);
      // Edge Function の実際のエラーメッセージを取得
      let detail = error.message;
      try {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.json();
          if (body?.error) detail = body.error;
        }
      } catch { /* context parse failed */ }
      throw new Error(detail);
    }
    const entry = (result?.entry || result) as Entry;
    console.debug('[api] createEntry success:', entry.id);
    return { entry };
  }

  async updateEntry(id: string, data: Partial<Entry>): Promise<Entry> {
    const { data: updated, error } = await supabase
      .from('entries')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return updated as Entry;
  }

  async deleteEntry(id: string): Promise<void> {
    const { error } = await supabase
      .from('entries')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  }

  // Semantic search (embedding similarity)
  async semanticSearch(params: {
    query: string;
    from?: string;
    to?: string;
    tags?: string;
    tagMode?: 'and' | 'or';
    moodFilter?: string;
    sentimentMin?: number;
    sentimentMax?: number;
    limit?: number;
  }): Promise<{ results: (Entry & { similarity: number })[] }> {
    const { data, error } = await supabase.functions.invoke('semantic-search', {
      body: params,
    });
    if (error) {
      let detail = error.message;
      try {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.json();
          if (body?.error) detail = body.error;
        }
      } catch { /* context parse failed */ }
      throw new Error(detail);
    }
    return { results: (data?.results || []) as (Entry & { similarity: number })[] };
  }

  // Search
  async search(query: string): Promise<Entry[]> {
    const userId = await this.getUserId();
    const sanitized = query.replace(/[%_\\]/g, '\\$&');
    const { data, error } = await supabase
      .from('entries')
      .select('*')
      .eq('user_id', userId)
      .or(`content.ilike.%${sanitized}%,title.ilike.%${sanitized}%,summary.ilike.%${sanitized}%`)
      .order('date', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []) as Entry[];
  }

  // Tags
  async getTags(): Promise<TagsResponse> {
    const userId = await this.getUserId();
    const { data: allTags } = await supabase
      .from('user_tags')
      .select('tag, usage_count, is_preset')
      .eq('user_id', userId)
      .order('usage_count', { ascending: false });

    // 初回利用: タグが0件ならプリセット30個をシード
    if (!allTags || allTags.length === 0) {
      const presetTags = [
        '仕事', '勉強', '健康', '運動', '食事', '趣味', '読書', '映画',
        '音楽', '旅行', '家族', '友人', '恋愛', 'お金', '買い物', '料理',
        '掃除', '天気', '季節', 'イベント', '目標', '振り返り', '感謝',
        'ストレス', 'リラックス', '睡眠', 'アイデア', '学び', '成長', '挑戦',
      ];
      await supabase.from('user_tags').insert(
        presetTags.map(tag => ({ user_id: userId, tag, usage_count: 0, is_preset: true }))
      );
      return {
        predefined: presetTags,
        custom: [],
        recent: [],
        usage: {},
        customCount: 0,
      };
    }

    const usage: Record<string, number> = {};
    const predefined: string[] = [];
    const custom: string[] = [];
    for (const t of allTags) {
      usage[t.tag] = t.usage_count;
      if (t.is_preset) {
        predefined.push(t.tag);
      } else {
        custom.push(t.tag);
      }
    }

    // Get recent tags from last 20 entries
    const { data: recentEntries } = await supabase
      .from('entries')
      .select('tags')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(20);

    const recentSet = new Set<string>();
    for (const e of recentEntries || []) {
      for (const t of e.tags || []) recentSet.add(t);
    }

    return { predefined, custom, recent: Array.from(recentSet), usage, customCount: custom.length };
  }

  async createCustomTag(tag: string): Promise<{ tag: string }> {
    const userId = await this.getUserId();
    const { error } = await supabase
      .from('user_tags')
      .insert({ user_id: userId, tag, usage_count: 0 });
    if (error && !error.message.includes('duplicate')) throw new Error(error.message);
    return { tag };
  }

  async deleteTag(tag: string): Promise<void> {
    const userId = await this.getUserId();
    const { error } = await supabase
      .from('user_tags')
      .delete()
      .eq('user_id', userId)
      .eq('tag', tag);
    if (error) throw new Error(error.message);
  }

  async renameTag(oldTag: string, newTag: string): Promise<{ affectedEntries: number; merged: boolean }> {
    const trimmed = newTag.trim();
    if (!trimmed || trimmed === oldTag) return { affectedEntries: 0, merged: false };
    const userId = await this.getUserId();
    const { data, error } = await supabase.rpc('rename_tag', {
      p_user_id: userId,
      p_old_tag: oldTag,
      p_new_tag: trimmed,
    });
    if (error) throw new Error(error.message);
    return data as { affectedEntries: number; merged: boolean };
  }

  // Preview
  async preview(content: string, mood?: string, tags?: string[], intensity?: string): Promise<PreviewResponse> {
    const { data, error } = await supabase.functions.invoke('preview', {
      body: { content, mood, tags, intensity },
    });
    if (error) throw new Error(error.message);
    return data as PreviewResponse;
  }

  // Search with advanced filters
  async searchEntries(params: {
    q?: string; tags?: string; from?: string; to?: string;
    limit?: number; cursor?: string; tagMode?: 'and' | 'or';
    moodFilter?: string; sentimentMin?: number; sentimentMax?: number;
  }) {
    const userId = await this.getUserId();
    let query = supabase
      .from('entries')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false });

    if (params.q) {
      const sanitized = params.q.replace(/[%_\\]/g, '\\$&');
      query = query.or(`content.ilike.%${sanitized}%,title.ilike.%${sanitized}%`);
    }
    if (params.from) query = query.gte('date', params.from);
    if (params.to) query = query.lte('date', params.to);
    if (params.moodFilter) query = query.eq('mood', params.moodFilter);
    if (typeof params.sentimentMin === 'number') query = query.gte('sentiment_score', params.sentimentMin);
    if (typeof params.sentimentMax === 'number') query = query.lte('sentiment_score', params.sentimentMax);
    if (params.tags) {
      const tagArr = params.tags.split(',').map(t => t.trim());
      if (params.tagMode === 'and') {
        query = query.contains('tags', tagArr);
      } else {
        query = query.overlaps('tags', tagArr);
      }
    }

    const limit = params.limit || 20;
    query = query.limit(limit);
    if (params.cursor) {
      query = query.lt('date', params.cursor);
    }

    const { data, error } = await query;
    if (error) throw new Error(error.message);

    const entries = (data || []) as Entry[];
    const nextCursor = entries.length === limit ? entries[entries.length - 1]?.date || null : null;
    return { entries, nextCursor };
  }

  // Reviews
  async generateReview(period: string, fromDate?: string, toDate?: string) {
    const body: Record<string, string> = { period };
    if (fromDate) body.fromDate = fromDate;
    if (toDate) body.toDate = toDate;
    const { data, error } = await supabase.functions.invoke('generate-review', {
      body,
    });
    if (error) {
      // Edge Function のエラーレスポンスからメッセージを取得
      let detail = error.message;
      try {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.json();
          if (body?.error) detail = body.error;
        }
      } catch { /* context parse failed */ }
      throw new Error(detail);
    }
    return data?.review as ReviewResponse | null;
  }

  async getReviews(): Promise<ReviewMeta[]> {
    const userId = await this.getUserId();
    const { data, error } = await supabase
      .from('reviews')
      .select('id, period, from_date, to_date, generated_at')
      .eq('user_id', userId)
      .order('generated_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []).map(r => ({
      id: r.id,
      period: r.period,
      fromDate: r.from_date,
      toDate: r.to_date,
      generatedAt: r.generated_at,
    }));
  }

  async getReview(id: string): Promise<ReviewResponse> {
    const userId = await this.getUserId();
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single();
    if (error) throw new Error(error.message);
    return {
      id: data.id,
      period: data.period,
      fromDate: data.from_date,
      toDate: data.to_date,
      entryCount: data.entry_count,
      avgSentiment: data.avg_sentiment,
      topTags: data.top_tags,
      topIcons: data.top_icons,
      iconCounts: data.icon_counts,
      tagCounts: data.tag_counts,
      sentimentTimeline: data.sentiment_timeline,
      wordCounts: data.word_counts,
      aiSummary: data.ai_summary,
      aiInsight: data.ai_insight || undefined,
      insightRefs: data.insight_refs || undefined,
      hiddenConnections: data.hidden_connections || undefined,
      generatedAt: data.generated_at,
    };
  }

  async deleteReview(id: string): Promise<void> {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', id);
    if (error) throw new Error(error.message);
  }

  // Subscription
  async getSubscription(): Promise<Subscription> {
    const userId = await this.getUserId();
    const { data: profile, error: profileErr } = await supabase
      .from('profiles')
      .select('premium')
      .eq('id', userId)
      .maybeSingle();
    if (profileErr) console.error('[getSubscription] profile query error:', profileErr.message);

    const now = new Date();
    const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

    // 月間合計録音秒数を取得（RPC — auth.uid() で認証ユーザー自身のみ集計）
    let monthlySeconds = 0;
    const { data: durationData, error: durationErr } = await supabase.rpc('get_monthly_duration', {
      p_month_start: firstOfMonth,
    });
    if (durationErr) {
      console.error('[getSubscription] get_monthly_duration error:', durationErr.message);
    } else {
      monthlySeconds = typeof durationData === 'number' ? durationData : 0;
    }

    // 件数も後方互換のために取得
    const { count } = await supabase
      .from('entries')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .gte('date', firstOfMonth);

    const isPremium = profile?.premium === true;
    const monthlyCount = count || 0;
    const limit = isPremium ? 9999 : 10;
    const limitSeconds = isPremium ? Infinity : 600; // フリー: 月10分
    const remainingSeconds = isPremium ? Infinity : Math.max(0, 600 - monthlySeconds);

    return {
      plan: isPremium ? 'premium' : 'free',
      status: 'active',
      premium: isPremium,
      monthlyCount,
      limit,
      remaining: Math.max(0, limit - monthlyCount),
      monthlySeconds,
      limitSeconds,
      remainingSeconds,
    };
  }

  // Deepgram token
  async getDeepgramToken(): Promise<{ token: string }> {
    const { data, error } = await supabase.functions.invoke('deepgram-token');
    if (error) throw new Error(error.message);
    return data as { token: string };
  }

  // Activate premium code
  async activate(code: string): Promise<{ success: boolean }> {
    console.debug('[activate] invoking activate-code function...');
    const result = await Promise.race([
      supabase.functions.invoke('activate-code', { body: { code } }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('招待コードの認証がタイムアウトしました（15秒）')), 15000)
      ),
    ]);
    console.debug('[activate] result:', result);
    const { data, error } = result;
    if (error) {
      console.error('[activate] error:', error);
      // Edge Function のエラーレスポンスからメッセージを取得
      try {
        const body = typeof error === 'object' && error !== null && 'context' in error
          ? await (error as any).context?.json?.()
          : null;
        if (body?.error) throw new Error(body.error);
      } catch (e) {
        if (e instanceof Error && e.message !== (error as any).message) throw e;
      }
      throw new Error((error as any).message || '招待コードの認証に失敗しました');
    }
    return data as { success: boolean };
  }

  // Vocabulary (find → replace ルール)
  async getVocabularyRules(): Promise<{ rules: VocabRule[] }> {
    const userId = await this.getUserId();
    const { data, error } = await supabase
      .from('vocabulary')
      .select('find_text, replace_text')
      .eq('user_id', userId);
    if (error) throw new Error(error.message);
    return { rules: (data || []).map(v => ({ findText: v.find_text, replaceText: v.replace_text })) };
  }

  async addVocabularyRule(findText: string, replaceText: string): Promise<{ rules: VocabRule[] }> {
    const userId = await this.getUserId();
    const { error: upsertError } = await supabase
      .from('vocabulary')
      .upsert({ user_id: userId, find_text: findText, replace_text: replaceText }, { onConflict: 'user_id,find_text' });
    if (upsertError) throw new Error(upsertError.message);
    return this.getVocabularyRules();
  }

  async removeVocabularyRule(findText: string): Promise<{ rules: VocabRule[] }> {
    const userId = await this.getUserId();
    const { error } = await supabase
      .from('vocabulary')
      .delete()
      .eq('user_id', userId)
      .eq('find_text', findText);
    if (error) throw new Error(error.message);
    return this.getVocabularyRules();
  }

  /** 後方互換: 旧APIを使っている箇所向け */
  async getVocabulary(): Promise<{ words: string[] }> {
    const { rules } = await this.getVocabularyRules();
    return { words: rules.map(r => r.replaceText) };
  }

  // Stats (RPC関数でDB側集計 — 全エントリーの取得を回避)
  async getStats(): Promise<{ totalCharacters: number; totalEntries: number }> {
    const userId = await this.getUserId();
    const { data, error } = await supabase.rpc('get_user_stats', { p_user_id: userId });
    if (error) {
      // RPC未デプロイ時のフォールバック
      console.error('[getStats] rpc error, falling back:', error.message);
      const { data: entries, error: fallbackErr } = await supabase
        .from('entries')
        .select('content')
        .eq('user_id', userId);
      if (fallbackErr) throw new Error(fallbackErr.message);
      const list = entries || [];
      return {
        totalCharacters: list.reduce((sum, e) => sum + (e.content?.length || 0), 0),
        totalEntries: list.length,
      };
    }
    return data as { totalCharacters: number; totalEntries: number };
  }

  // Settings
  async getSettings(): Promise<{ settings: AppSettings }> {
    const userId = await this.getUserId();
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) console.error('[getSettings] query error:', error.message);
    return {
      settings: {
        tonePrompt: data?.tone_prompt || '',
        theme: data?.theme || 'default',
        aiTagSuggestion: data?.ai_tag_suggestion !== false,
      },
    };
  }

  async updateSettings(settings: Partial<AppSettings>): Promise<{ success: boolean }> {
    const userId = await this.getUserId();
    const updateData: Record<string, unknown> = {};
    if (settings.tonePrompt !== undefined) updateData.tone_prompt = settings.tonePrompt;
    if (settings.theme !== undefined) updateData.theme = settings.theme;
    if (settings.aiTagSuggestion !== undefined) updateData.ai_tag_suggestion = settings.aiTagSuggestion;

    const { error } = await supabase
      .from('user_settings')
      .update(updateData)
      .eq('user_id', userId);
    if (error) throw new Error(error.message);
    return { success: true };
  }

  // Stripe Checkout
  async createCheckout(interval: 'month' | 'year'): Promise<{ checkoutUrl: string }> {
    const { data, error } = await supabase.functions.invoke('stripe-checkout', {
      body: { interval, returnUrl: window.location.origin },
    });
    if (error) throw new Error(error.message);
    return data as { checkoutUrl: string };
  }

  // Stripe Customer Portal
  async createPortalSession(): Promise<{ portalUrl: string }> {
    const { data, error } = await supabase.functions.invoke('stripe-portal', {
      body: { returnUrl: window.location.origin },
    });
    if (error) throw new Error(error.message);
    return data as { portalUrl: string };
  }

  // Transcribe (Whisper API via Edge Function)
  async transcribe(audio: Blob, previousText?: string): Promise<{ text: string }> {
    const formData = new FormData();
    formData.append('audio', audio, 'audio.webm');
    if (previousText) formData.append('previousText', previousText);
    const { data, error } = await supabase.functions.invoke('transcribe', {
      body: formData,
    });
    if (error) throw new Error(error.message);
    return data as { text: string };
  }

  // Feedback
  async submitFeedback(body: string, rating?: number): Promise<void> {
    const userId = await this.getUserId();
    const row: Record<string, unknown> = { user_id: userId, body };
    if (rating != null) row.rating = rating;
    const { error } = await supabase
      .from('feedback')
      .insert(row);
    if (error) throw new Error(error.message);
  }

  // Admin Dashboard
  async getAdminDashboard(): Promise<AdminDashboardResponse> {
    const { data, error } = await supabase.functions.invoke('admin-dashboard');
    if (error) throw new Error(error.message);
    return data as AdminDashboardResponse;
  }

  // Admin Tickets
  async getAdminTickets(status?: string): Promise<{ tickets: AdminTicket[] }> {
    const params = status ? `?status=${status}` : '';
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/admin-tickets${params}`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    return await res.json();
  }

  async updateTicket(id: string, status?: string, adminNotes?: string): Promise<{ ticket: AdminTicket }> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/admin-tickets`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, status, admin_notes: adminNotes }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    return await res.json();
  }

  // Journal (alternative entry creation via text)
  async createJournal(data: FormData): Promise<Entry> {
    const { data: result, error } = await supabase.functions.invoke('create-journal', {
      body: data,
    });
    if (error) throw new Error(error.message);
    return result as Entry;
  }

  // Entry images
  async uploadEntryImage(entryId: string, file: File): Promise<string> {
    const userId = await this.getUserId();
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${userId}/${entryId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
    console.debug('[api] uploadEntryImage:', { path, type: file.type, size: file.size });

    const { error } = await supabase.storage
      .from('entry-images')
      .upload(path, file, { contentType: file.type });
    if (error) {
      console.error('[api] storage upload failed:', error);
      throw new Error(error.message);
    }
    console.debug('[api] upload success, creating signed URL...');

    // Generate signed URL (1 year expiry)
    const { data: urlData, error: urlError } = await supabase.storage
      .from('entry-images')
      .createSignedUrl(path, 60 * 60 * 24 * 365);
    if (urlError || !urlData?.signedUrl) {
      console.error('[api] createSignedUrl failed:', urlError);
      throw new Error(urlError?.message || 'URL生成に失敗しました');
    }

    console.debug('[api] signed URL created');
    return urlData.signedUrl;
  }

  async deleteEntryImage(_entryId: string, url: string): Promise<void> {
    const userId = await this.getUserId();
    // Extract storage path from signed URL
    const match = url.match(/entry-images\/([^?]+)/);
    if (!match) throw new Error('Invalid image URL');
    const path = decodeURIComponent(match[1]);

    // Verify the path belongs to this user
    if (!path.startsWith(userId)) throw new Error('Unauthorized');

    const { error } = await supabase.storage
      .from('entry-images')
      .remove([path]);
    if (error) throw new Error(error.message);
  }
}

// Types used by API client
export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  plan?: string;
  role?: string;
  featureFlags?: Record<string, boolean>;
}

export interface Entry {
  id: string;
  date: string;
  content: string;
  title?: string;
  icon?: string;
  mood?: string;
  tags?: string[];
  summary?: string;
  sentiment_score?: number;
  ai_comment?: string;
  ai_insight?: string;
  duration_seconds?: number;
  image_urls?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface TagsResponse {
  predefined: string[];
  custom: string[];
  recent: string[];
  usage: Record<string, number>;
  customCount: number;
}

export interface PreviewResponse {
  title: string;
  content: string;
  summary: string;
  tags: string[];
  icon?: string;
  sentimentScore: number;
}

export interface ReviewResponse {
  id: string;
  period: string;
  fromDate: string;
  toDate: string;
  entryCount: number;
  avgSentiment: number;
  topTags: string[];
  topIcons: string[];
  iconCounts: Record<string, number>;
  /** @deprecated Use topIcons instead */
  topMoods?: string[];
  /** @deprecated Use iconCounts instead */
  moodCounts?: Record<string, number>;
  tagCounts: Record<string, number>;
  sentimentTimeline?: Array<{ date: string; score: number }>;
  wordCounts?: Record<string, number>;
  aiSummary: string;
  aiInsight?: string;
  insightRefs?: Array<{ ref: number; entryId: string; date: string }>;
  hiddenConnections?: Array<{
    entryA: { id: string; date: string; title: string };
    entryB: { id: string; date: string; title: string };
    similarity: number;
    sharedTags: string[];
    explanation?: string;
  }>;
  generatedAt: string;
}

export interface ReviewMeta {
  id: string;
  period: string;
  fromDate: string;
  toDate: string;
  generatedAt: string;
}

export interface Subscription {
  plan: string;
  status: string;
  premium: boolean;
  monthlyCount: number;
  limit: number;
  remaining: number;
  // 時間ベース制限
  monthlySeconds: number;
  limitSeconds: number;
  remainingSeconds: number;
  // legacy compat
  entries_count?: number;
  entries_limit?: number;
}

export interface AppSettings {
  tonePrompt?: string;
  previewStyle?: string;
  theme?: string;
  aiTagSuggestion?: boolean;
}

export interface CreateEntryResult {
  entry: Entry;
}

export interface AdminDashboardResponse {
  apiBalances: {
    openrouter: { credits?: number; usage?: number; remaining?: number; error?: string };
    deepgram: { balance?: number; currency?: string; error?: string };
    stripe: { available?: number; pending?: number; currency?: string; error?: string };
  };
  business: {
    totalUsers: number;
    activeUsersThisMonth: number;
    premiumUsers: number;
    mrr?: number;
    entriesThisMonth: number;
    totalEntries: number;
    monthlyDurationSeconds: number;
  };
  tickets: {
    open: number;
    inProgress: number;
  };
  charts: {
    dailyEntries: Array<{ date: string; count: number }>;
    userGrowth: Array<{ date: string; count: number }>;
  };
}

export interface AdminTicket {
  id: string;
  user_id: string;
  body: string;
  rating: number | null;
  status: string;
  admin_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  user: {
    display_name: string | null;
    email: string | null;
  };
}

export const api = new ApiClient();
